/**
 * Phase 5 — Pakistani legal pleading layout heuristics.
 * Applies only when OOXML / mammoth did not already set formatting.
 */

export interface LegalHeuristicsStats {
  blocksScanned: number;
  centerApplied: number;
  rightAlignApplied: number;
  emphasisApplied: number;
  firstLineIndentApplied: number;
  hangingIndentApplied: number;
  partyIndentApplied: number;
  spacingApplied: number;
  totalApplied: number;
}

const FIRST_LINE_INDENT_PX = 48;
const HANGING_BLOCK_PX = 48;
const PARTY_INDENT_PX = 24;
const COURT_BLOCK_SPACING_PX = 12;
const SIGNATURE_TOP_SPACING_PX = 24;

const COURT_HEADING =
  /^(before\s+the\s+honou?rable|in\s+the\s+(honou?rable\s+)?(court|high\s+court|supreme\s+court|lahore\s+high\s+court|islamabad\s+high\s+court|sessions?\s+court|district\s+court|family\s+court)|in\s+the\s+matter\s+of|in\s+re:)/i;

const CASE_TITLE =
  /^(civil|criminal|constitutional|family)\s+(original|misc|appeal|revision|review|suit|petition|appeal)/i;

const VERSUS_LINE = /^(versus|v\.|vs\.?)$/i;

const PARTY_LABEL =
  /^(petitioner(s)?|plaintiff(s)?|applicant(s)?|appellant(s)?|defendant(s)?|respondent(s)?|accused|opposite\s+party|judgment\s+debtor|judgment\s+creditor)(\s*:|\s*$)/i;

const RIGHT_SIGNATURE =
  /^(counsel\s+for|through\s+counsel|learned\s+counsel\s+for|advocate\s+for|attorney\s+for|for\s+petitioner|for\s+respondent|for\s+plaintiff|for\s+defendant|assistant\s+advocate|deputy\s+registrar|verified\s+at)/i;

const THROUGH_COUNSEL =
  /^through\s+(learned\s+)?counsel/i;

const EMPHASIS_HEADING =
  /^(most\s+respectfully\s+sheweth|prayer|prayers|relief\s+sought|grounds(\s+of\s+\w+)?|brief\s+facts|facts(\s+of\s+the\s+case)?|preliminary\s+objection|title\s+of\s+petition|index)/i;

const PRAYER_BODY =
  /^(it\s+is\s+prayed|in\s+the\s+circumstances|it\s+is,\s+therefore|may\s+it\s+please)/i;

const NUMBERED_LEGAL =
  /^(\d+\.|\([a-z]\)|\([ivxlcdm]+\))\s+/i;

const AFFIDAVIT_OPENING =
  /^i,?\s+.+\s+do\s+hereby\s+(solemnly\s+)?(state|affirm|declare|verify)/i;

function emptyStats(): LegalHeuristicsStats {
  return {
    blocksScanned: 0,
    centerApplied: 0,
    rightAlignApplied: 0,
    emphasisApplied: 0,
    firstLineIndentApplied: 0,
    hangingIndentApplied: 0,
    partyIndentApplied: 0,
    spacingApplied: 0,
    totalApplied: 0,
  };
}

function normalizedText(el: HTMLElement): string {
  return (el.textContent ?? "").replace(/\s+/g, " ").trim();
}

function hasAlignment(el: HTMLElement): boolean {
  const align = el.style.textAlign?.toLowerCase();
  if (align && align !== "start") return true;
  const cls = (el.className || "").toLowerCase();
  return (
    cls.includes("center") ||
    cls.includes("right") ||
    cls.includes("text-center") ||
    cls.includes("text-right")
  );
}

function hasIndent(el: HTMLElement): boolean {
  const ml = el.style.marginLeft;
  const ti = el.style.textIndent;
  return !!(ml && ml !== "0px" && ml !== "0") || !!(ti && ti !== "0px" && ti !== "0");
}

function hasVerticalSpacing(el: HTMLElement): boolean {
  return !!(el.style.marginTop || el.style.marginBottom);
}

function isInsideListItem(el: HTMLElement): boolean {
  return !!el.closest("li, ol, ul");
}

function wrapEmphasis(el: HTMLElement): boolean {
  if (el.querySelector("strong, b")) return false;
  const text = el.textContent ?? "";
  if (!text.trim()) return false;
  el.innerHTML = `<strong>${text}</strong>`;
  return true;
}

function applyCenter(el: HTMLElement): boolean {
  if (hasAlignment(el)) return false;
  el.style.textAlign = "center";
  return true;
}

function applyRight(el: HTMLElement): boolean {
  if (hasAlignment(el)) return false;
  el.style.textAlign = "right";
  return true;
}

function applyFirstLineIndent(el: HTMLElement, px = FIRST_LINE_INDENT_PX): boolean {
  if (hasIndent(el)) return false;
  el.style.textIndent = `${px}px`;
  return true;
}

function applyHangingIndent(
  el: HTMLElement,
  marginPx = HANGING_BLOCK_PX,
  hangingPx = HANGING_BLOCK_PX,
): boolean {
  if (hasIndent(el)) return false;
  el.style.marginLeft = `${marginPx}px`;
  el.style.textIndent = `-${hangingPx}px`;
  return true;
}

function applyPartyIndent(el: HTMLElement): boolean {
  if (hasIndent(el)) return false;
  el.style.marginLeft = `${PARTY_INDENT_PX}px`;
  return true;
}

function applyCourtSpacing(el: HTMLElement): boolean {
  if (hasVerticalSpacing(el)) return false;
  el.style.marginTop = `${COURT_BLOCK_SPACING_PX}px`;
  el.style.marginBottom = `${COURT_BLOCK_SPACING_PX}px`;
  return true;
}

function applySignatureSpacing(el: HTMLElement): boolean {
  if (hasVerticalSpacing(el)) return false;
  el.style.marginTop = `${SIGNATURE_TOP_SPACING_PX}px`;
  return true;
}

function isCourtHeading(text: string): boolean {
  return (
    COURT_HEADING.test(text) ||
    CASE_TITLE.test(text) ||
    VERSUS_LINE.test(text)
  );
}

/**
 * Apply Pakistani legal layout heuristics (preserve mode only).
 */
export function applyLegalParagraphHeuristics(
  root: ParentNode,
): LegalHeuristicsStats {
  const stats = emptyStats();

  root.querySelectorAll("p, h1, h2, h3, h4, h5, h6").forEach((node) => {
    if (!(node instanceof HTMLElement)) return;
    if (node.closest("table")) return;
    const text = normalizedText(node);
    if (!text) return;

    stats.blocksScanned++;

    if (isCourtHeading(text)) {
      if (applyCenter(node)) stats.centerApplied++;
      if (applyCourtSpacing(node)) stats.spacingApplied++;
    } else if (THROUGH_COUNSEL.test(text)) {
      if (applyHangingIndent(node, HANGING_BLOCK_PX, PARTY_INDENT_PX)) {
        stats.hangingIndentApplied++;
      }
    } else if (!hasAlignment(node) && RIGHT_SIGNATURE.test(text)) {
      if (applyRight(node)) stats.rightAlignApplied++;
      if (applySignatureSpacing(node)) stats.spacingApplied++;
    } else if (PARTY_LABEL.test(text)) {
      if (applyPartyIndent(node)) stats.partyIndentApplied++;
    }

    if (EMPHASIS_HEADING.test(text)) {
      if (wrapEmphasis(node)) stats.emphasisApplied++;
      if (applyFirstLineIndent(node)) stats.firstLineIndentApplied++;
    } else if (PRAYER_BODY.test(text)) {
      if (applyFirstLineIndent(node)) stats.firstLineIndentApplied++;
    } else if (
      AFFIDAVIT_OPENING.test(text) &&
      applyFirstLineIndent(node, FIRST_LINE_INDENT_PX)
    ) {
      stats.firstLineIndentApplied++;
    } else if (
      NUMBERED_LEGAL.test(text) &&
      !isInsideListItem(node) &&
      applyHangingIndent(node)
    ) {
      stats.hangingIndentApplied++;
    }
  });

  stats.totalApplied =
    stats.centerApplied +
    stats.rightAlignApplied +
    stats.emphasisApplied +
    stats.firstLineIndentApplied +
    stats.hangingIndentApplied +
    stats.partyIndentApplied +
    stats.spacingApplied;

  return stats;
}
